import { NotificationSource } from '../types/Notifications/types';

export type NotificationTrackingEventType = 'notification-seen';
export type NotificationTrackingEventMode = 'single' | 'bulk';

export interface NotificationSeenTrackingPayload {
  count: number;
  mode: NotificationTrackingEventMode;
  notificationIds: string[];
  notificationTypes: string[];
  notificationSources: NotificationSource[];
  seenAt: string;
}

export type NotificationSeenTrackingItem = {
  id: string;
  type: string;
  source: NotificationSource;
};

type NotificationTrackingCallback = (payload: NotificationSeenTrackingPayload) => void;

class NotificationTrackingEventEmitter {
  private listeners: Map<
    NotificationTrackingEventType,
    Set<NotificationTrackingCallback>
  > = new Map();

  on(
    event: NotificationTrackingEventType,
    callback: NotificationTrackingCallback,
  ): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event)!.add(callback);

    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  emit(
    event: NotificationTrackingEventType,
    payload: NotificationSeenTrackingPayload,
  ): void {
    this.listeners.get(event)?.forEach((callback) => {
      try {
        callback(payload);
      } catch (error) {
        console.error(
          `[NotificationTrackingEvents] Error in listener for "${event}"`,
          error,
        );
      }
    });
  }

  off(
    event: NotificationTrackingEventType,
    callback: NotificationTrackingCallback,
  ): void {
    this.listeners.get(event)?.delete(callback);
  }
}

const uniqueStrings = (values: string[]): string[] =>
  Array.from(new Set(values.filter(Boolean)));

const uniqueSources = (values: NotificationSource[]): NotificationSource[] =>
  Array.from(new Set(values));

export const buildNotificationSeenTrackingPayload = (
  items: NotificationSeenTrackingItem[],
  mode: NotificationTrackingEventMode,
): NotificationSeenTrackingPayload => ({
  count: items.length,
  mode,
  notificationIds: uniqueStrings(items.map((item) => item.id)),
  notificationTypes: uniqueStrings(items.map((item) => item.type)),
  notificationSources: uniqueSources(items.map((item) => item.source)),
  seenAt: new Date().toISOString(),
});

export const notificationTrackingEvents = new NotificationTrackingEventEmitter();

export const NOTIFICATION_TRACKING_EVENTS = {
  SEEN: 'notification-seen' as NotificationTrackingEventType,
};
